import * as React from "react";
import { FastField } from "formik";
import { FormGroup, SelectAdv, FormUpload } from "@lib/components";
import { Button } from "@lib/components";
import { RotateLoader } from "@lib/components";
import { TabSelect } from "@lib/components";
import { Input } from "@lib/components";
import { Switch } from "@lib/components";
import { ServiceOrderTimeFields, ServiceHoursField, CustomCheckoutField } from "./base";
import { MobxComponent } from "../../../../../../../mobx/components/index";
import { inject, observer } from "mobx-react";
import { RestaurantForm } from "../../../../../../../mobx/components/restaurant-form";
import { UI } from "../../../../../../../core/ui";
import { updateRestaurantNotificationOptions } from "../../../../../../../core/libs/receipt";
import { CoreUtils } from "@lib/common";
import { SUPPORTED_LANGUAGE_CODES, TranslationsEN, isSupportedLanguage } from "@lib/intl";

/** "Venue default" + every bundled storefront language, English first then by local name. */
const WIDGET_LANGUAGE_OPTIONS: Array<{ label: string; value: string }> = [
	{ label: "Venue default", value: "" },
	...[...SUPPORTED_LANGUAGE_CODES]
		.sort((a, b) => {
			if (a === "en") return -1;
			if (b === "en") return 1;
			return languageLabel(a).localeCompare(languageLabel(b));
		})
		.map((code) => ({ label: `${languageLabel(code)} (${code})`, value: code })),
];

function languageLabel(code: string): string {
	const names = (TranslationsEN as { languages?: Record<string, string> }).languages || {};
	return names[code] || code;
}

interface Props { }
interface State {
	tab: string;
	widgetTab: "website" | "widget";
	durationOptionsText: string;
}
type FormValues = T.Schema.Restaurant.RestaurantSchema["settings"]["services"]["table_booking"];

// Stand-in used when the restaurant has neither a custom domain nor a reseller
// subdomain to build one from. A snippet pointing here can never load.
const PLACEHOLDER_STOREFRONT_URL = "https://your-storefront-domain";

const AUTO_CONFIRM_STRATEGY_OPTIONS = [
	{ label: "Off (manual confirm)", value: "off" },
	{ label: "Always auto-confirm", value: "always" },
	{ label: "Smart (threshold)", value: "smart" },
];

@inject("store") @observer
export class SettingsFormServicesTableBooking extends MobxComponent<Props, State> {

	initialValues: FormValues;

	constructor(props: Props) {
		super(props);
		const r = this.injected.store.restaurant!;
		this.state = {
			tab: "0",
			widgetTab: "website",
			durationOptionsText: ((r.settings.services.table_booking.widget?.branding?.booking_duration_options || []) as number[]).join(","),
		};
		this.initialValues = r.settings.services.table_booking;
		const topNav = r.website?.sections?.top_nav as T.ObjectAny | undefined;
		const websiteLogo = topNav?.image_logo || topNav?.images?.logo;
		const websiteLogoUrl = websiteLogo
			? CoreUtils.image.uc(websiteLogo, { format: "auto" })
			: "";
		if (websiteLogoUrl && !(this.initialValues.widget?.branding?.logo_url || "").trim()) {
			this.initialValues = {
				...this.initialValues,
				widget: {
					...(this.initialValues.widget || {}),
					branding: {
						...(this.initialValues.widget?.branding || {}),
						logo_url: websiteLogoUrl,
					},
				},
			};
		}
	}

	getStorefrontBaseUrl() {
		const { restaurant: r, reseller } = this.injected.store;
		if (!r) return PLACEHOLDER_STOREFRONT_URL;
		if (r.domain) return `https://${r.domain}`;
		if (r.subdomain && reseller?.store_host) return `https://${r.subdomain}.${reseller.store_host}`;
		return PLACEHOLDER_STOREFRONT_URL;
	}

	copyText = (text: string) => {
		if (navigator.clipboard) {
			navigator.clipboard.writeText(text);
			UI.notification.success("Copied");
			return;
		}
		UI.notification.error("Clipboard unavailable");
	};

	parseNumberCsv(value: string) {
		return value
			.split(",")
			.map((x) => Number(x.trim()))
			.filter((x) => Number.isFinite(x) && x > 0);
	}

	colProps = {
		className: "col",
		small_title: true,
		no_border: true,
	};

	renderInstallTab(form: any, setFieldValue: (field: string, value: any) => void) {
		const { widgetTab } = this.state;
		const r = this.injected.store.restaurant!;
		const w = form.values.widget || {};
		const b = w.branding || {};
		const websiteColors = r.website?.colors || {};
		const websitePrimary = b.primary_color || websiteColors.primary || "#a85d44";
		const widgetPrimary = w.accent_color || b.primary_color || websiteColors.primary || "#a85d44";
		const title = b.restaurant_name?.trim() || r.name || "Restaurant";
		const topNav = r.website?.sections?.top_nav as T.ObjectAny | undefined;
		const websiteLogo = topNav?.image_logo || topNav?.images?.logo;
		const websiteLogoUrl = websiteLogo
			? CoreUtils.image.uc(websiteLogo, { format: "auto" })
			: "";
		const logo = b.logo_url?.trim() || websiteLogoUrl;
		const letter = title.trim().charAt(0).toUpperCase() || "?";
		const baseUrl = this.getStorefrontBaseUrl();
		const bookingUrl = `${baseUrl}/reserve`;
		const widgetPosition = w.widget_position || "bottom-right";
		const widgetButtonStyle = w.widget_button_style || "rounded";
		const welcomeMessage = w.welcome_message || "Book a table";
		const embedTag = (w.embed_tag || "").trim();
		const embedTagAttr = embedTag
			? ` data-tag="${embedTag.replace(/"/g, "&quot;")}"`
			: "";
		// Only emit languages the storefront has a bundle for (same list the form offers).
		const widgetLanguage = isSupportedLanguage(w.language) ? w.language : "";
		const langAttr = widgetLanguage ? ` data-lang="${widgetLanguage}"` : "";
		const hasStorefrontDomain = baseUrl !== PLACEHOLDER_STOREFRONT_URL;
		const script = `<script src="${baseUrl}/widget.js" data-booking-url="${bookingUrl}" data-primary-color="${widgetPrimary}" data-position="${widgetPosition}" data-button-style="${widgetButtonStyle}" data-welcome-message="${welcomeMessage.replace(/"/g, "&quot;")}"${embedTagAttr}${langAttr} async></script>`;
		const stackLayout = (this.injected.store.view.screen_width || 1024) < 1024;
		const settingRow = (content: React.ReactNode, opts?: { borderTop?: boolean }) => (
			<div style={{ width: "100%", boxSizing: "border-box", borderTop: opts?.borderTop === false ? undefined : "1px dashed #e2e8f0", paddingTop: "0.75rem", marginTop: opts?.borderTop === false ? 0 : "0.75rem" }}>
				{content}
			</div>
		);
		const pairRow = (content: React.ReactNode, cols: 1 | 2 = 2) => (
			<div style={{ width: "100%", boxSizing: "border-box" as const, display: "grid", gridTemplateColumns: stackLayout || cols === 1 ? "1fr" : "1fr 1fr", gap: "0.75rem", paddingBottom: "20px", marginBottom: "20px", borderBottom: "1px dashed #e2e8f0" }}>
				{React.Children.toArray(content).map((child, i) => (
					<div key={i} style={{ minWidth: 0 }}>
						{child}
					</div>
				))}
			</div>
		);
		const twoCol = { display: "grid", gridTemplateColumns: stackLayout ? "1fr" : "1fr 1fr", gap: "0.75rem" };

		const renderBrandFields = () => (
			<div className="m-t-4">
				{pairRow(
					<>
						<FastField
							name="widget.branding.restaurant_name"
							render={({ field }: any) => (
								<FormGroup {...this.colProps} optional={true} title="Restaurant Name" help="Displayed on the booking website and widget header. Defaults to your restaurant name.">
									<Input type="text" {...field} value={field.value || ""} />
								</FormGroup>
							)}
						/>
						<FastField
							name="widget.branding.address"
							render={({ field }: any) => (
								<FormGroup {...this.colProps} optional={true} title="Address" help="Shown beneath the restaurant name on the booking page.">
									<Input type="text" {...field} value={field.value || ""} />
								</FormGroup>
							)}
						/>
					</>
				, 1)}
				{pairRow(
					<>
						<FastField
							name="widget.branding.cuisine"
							render={({ field }: any) => (
								<FormGroup {...this.colProps} optional={true} title="Cuisine" help="Optional label shown on the booking page hero.">
									<Input type="text" placeholder="Italian" {...field} value={field.value || ""} />
								</FormGroup>
							)}
						/>
						<FastField
							name="widget.branding.price_range"
							render={({ field }: any) => (
								<FormGroup {...this.colProps} optional={true} title="Price Range" help="Optional label shown alongside cuisine on the booking page.">
									<Input type="text" placeholder="$$$$" {...field} value={field.value || ""} />
								</FormGroup>
							)}
						/>
					</>
				, 1)}
				{pairRow(
					<>
						<FastField
							name="widget.branding.logo_url"
							render={({ field }: any) => (
								<FormGroup {...this.colProps} optional={true} title="Logo Image" help="Logo shown on the booking website and widget. Upload an image or paste a custom URL.">
									<FormUpload
										maxSize={300}
										imagesOnly={true}
										customSource={true}
										value={[CoreUtils.image.ucReverse((field.value || websiteLogoUrl || "").trim()) || null]}
										onRemove={() => setFieldValue("widget.branding.logo_url", "")}
										onChange={(files) => setFieldValue("widget.branding.logo_url", CoreUtils.image.uc(files[0]) || "")}
									/>
								</FormGroup>
							)}
						/>
						<FastField
							name="widget.branding.banner_url"
							render={({ field }: any) => (
								<FormGroup {...this.colProps} optional={true} title="Banner Image" help="Hero image on the booking page header. Upload an image or paste a custom URL.">
									<FormUpload
										maxSize={1000}
										imagesOnly={true}
										customSource={true}
										value={[CoreUtils.image.ucReverse((field.value || "").trim()) || null]}
										onRemove={() => setFieldValue("widget.branding.banner_url", "")}
										onChange={(files) => setFieldValue("widget.branding.banner_url", CoreUtils.image.uc(files[0]) || "")}
									/>
								</FormGroup>
							)}
						/>
					</>
				, 1)}
			</div>
		);

		const renderWebsiteControls = () => (
			<div>
				<div style={twoCol}>
					<FastField
						name="widget.branding.show_email_field"
						render={({ field }: any) => (
							<FormGroup {...this.colProps} title="Show Email Field" help="Display an email input on the booking form.">
								<Switch id="booking-site-show-email" checked={field.value !== false} onChange={(e) => setFieldValue("widget.branding.show_email_field", e.target.checked)} />
							</FormGroup>
						)}
					/>
					<FastField
						name="widget.branding.require_email"
						render={({ field }: any) => (
							<FormGroup {...this.colProps} title="Require Email" help="Make the email field mandatory before guests can submit a booking.">
								<Switch id="booking-site-require-email" checked={field.value !== false} onChange={(e) => setFieldValue("widget.branding.require_email", e.target.checked)} />
							</FormGroup>
						)}
					/>
				</div>
				{settingRow(
					<div style={twoCol}>
						<FastField
							name="widget.branding.enable_booking_duration"
							render={({ field }: any) => (
								<FormGroup {...this.colProps} title="Booking Duration Options" help="Let guests choose how long their reservation lasts.">
									<Switch id="booking-site-duration-enabled" checked={!!field.value} onChange={(e) => setFieldValue("widget.branding.enable_booking_duration", e.target.checked)} />
								</FormGroup>
							)}
						/>
						<FormGroup {...this.colProps} title="Duration Values" help="Comma-separated minutes shown when duration options are enabled (e.g. 60,90,120).">
							<Input
								type="text"
								placeholder="60,90,120"
								style={{ position: "relative", zIndex: 2 }}
								value={this.state.durationOptionsText}
								onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
									const next = e.target.value;
									this.setState({ durationOptionsText: next });
									setFieldValue("widget.branding.booking_duration_options", this.parseNumberCsv(next));
								}}
							/>
						</FormGroup>
					</div>
				)}
			</div>
		);

		const renderWidgetControls = () => (
			<div>
				{pairRow(
					<>
						<FastField
							name="widget.embed_enabled"
							render={({ field }: any) => (
								<FormGroup {...this.colProps} title="Embed / Widget Enabled" help="Show a floating reservation button on external websites that load the embed script.">
									<Switch id="table-booking-widget-embed-enabled" checked={field.value === true} onChange={(e) => setFieldValue("widget.embed_enabled", e.target.checked)} />
								</FormGroup>
							)}
						/>
						<FastField
							name="widget.welcome_message"
							render={({ field }: any) => (
								<FormGroup {...this.colProps} optional={true} title="Welcome Message" help="Short text shown on the floating widget launcher.">
									<Input type="text" placeholder="Book a table" {...field} value={field.value || ""} />
								</FormGroup>
							)}
						/>
					</>
				,1)}
				{pairRow(
					<FastField
						name="widget.embed_tag"
						render={({ field }: any) => (
							<FormGroup
								{...this.colProps}
								optional={true}
								title="Embed tag"
								help='Optional identifier added to the install snippet as data-tag (e.g. partner site or campaign). Forwarded on the booking URL as query param tag=. You can also set data-tag manually on the script tag.'
							>
								<Input type="text" placeholder="homepage-widget" {...field} value={field.value || ""} />
							</FormGroup>
						)}
					/>
				,1)}
				{pairRow(
					<>
						<FormGroup {...this.colProps} title="Button Style" help="Shape of the floating reservation button.">
							<SelectAdv
								type="single"
								value={widgetButtonStyle}
								options={[
									{ label: "Rounded", value: "rounded" },
									{ label: "Square", value: "square" },
									{ label: "Pill", value: "pill" },
								]}
								onChange={(value: string) => setFieldValue("widget.widget_button_style", value)}
							/>
						</FormGroup>
						<FormGroup {...this.colProps} title="Widget Position" help="Corner of the screen where the floating button appears.">
							<SelectAdv
								type="single"
								value={widgetPosition}
								options={[
									{ label: "Bottom Right", value: "bottom-right" },
									{ label: "Bottom Left", value: "bottom-left" },
								]}
								onChange={(value: string) => setFieldValue("widget.widget_position", value)}
							/>
						</FormGroup>
					</>
				,1)}
				{pairRow(
					<FormGroup
						{...this.colProps}
						optional={true}
						title="Widget Language"
						help="Language the embedded booking form opens in (guests can still switch). Venue default uses your region locale."
					>
						<SelectAdv
							type="single"
							value={widgetLanguage}
							options={WIDGET_LANGUAGE_OPTIONS}
							onChange={(value: string) => setFieldValue("widget.language", value === "" ? undefined : value)}
						/>
					</FormGroup>
				,1)}
				{pairRow(
					<FastField
						name="widget.accent_color"
						render={({ field }: any) => (
							<FormGroup {...this.colProps} optional={true} title="Accent Color" help="Primary color for the floating widget button and booking panel.">
								<div className="flex-line gap-2">
									<Input
										type="color"
										style={{ width: 48, height: 40, padding: 4, cursor: "pointer" }}
										value={field.value || widgetPrimary}
										onChange={(e) => setFieldValue("widget.accent_color", e.target.value)}
									/>
									<Input type="text" placeholder="#a85d44" {...field} value={field.value || ""} />
								</div>
							</FormGroup>
						)}
					/>
				, 1)}
			</div>
		);

		const websitePreview = (
			<div style={{ width: "100%", maxWidth: 420, borderRadius: 28, overflow: "hidden", background: "#fff", boxShadow: "0 24px 54px rgba(15,23,42,0.18)", display: "grid", gridTemplateColumns: stackLayout ? "1fr" : "1fr 1fr" }}>
				<div
					style={{
						padding: "1.25rem",
						display: "flex",
						alignItems: "flex-end",
						color: "#fff",
						minHeight: stackLayout ? 160 : 280,
						background: b.banner_url?.trim()
							? `linear-gradient(180deg, rgba(15,23,42,0.1), rgba(15,23,42,0.82)), url("${b.banner_url.trim()}") center / cover`
							: `linear-gradient(135deg, ${websitePrimary}, #111827)`,
					}}
				>
					<div>
						<div style={{ display: "inline-flex", padding: "0.28rem 0.5rem", borderRadius: 999, background: "rgba(255,255,255,0.16)", border: "1px solid rgba(255,255,255,0.22)", fontSize: 8, fontWeight: 900, letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: 12 }}>
							Reservations
						</div>
						<div style={{ width: 46, height: 46, borderRadius: 15, background: "rgba(255,255,255,0.14)", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", border: "1px solid rgba(255,255,255,0.2)", fontWeight: 900, marginBottom: 10 }}>
							{logo ? <img src={logo} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : letter}
						</div>
						<p className="m-b-1 font-bold" style={{ color: "#fff", fontSize: 18, lineHeight: 1.05 }}>{title}</p>
						<p className="tiny m-b-2" style={{ color: "rgba(255,255,255,0.78)", lineHeight: 1.35 }}>{b.address?.trim() || r.location?.address || ""}</p>
						{(b.cuisine?.trim() || b.price_range?.trim()) ? (
							<div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
								{b.cuisine?.trim() ? <span style={{ display: "inline-flex", padding: "0.22rem 0.55rem", borderRadius: 999, background: "rgba(255,255,255,0.16)", border: "1px solid rgba(255,255,255,0.28)", fontSize: 9, fontWeight: 800 }}>{b.cuisine.trim()}</span> : null}
								{b.price_range?.trim() ? <span style={{ display: "inline-flex", padding: "0.22rem 0.55rem", borderRadius: 999, background: "rgba(255,255,255,0.16)", border: "1px solid rgba(255,255,255,0.28)", fontSize: 9, fontWeight: 800 }}>{b.price_range.trim()}</span> : null}
							</div>
						) : null}
					</div>
				</div>
				<div style={{ padding: "1rem", background: "#fff" }}>
					<p className="tiny font-bold text-muted m-b-1" style={{ letterSpacing: "0.12em", textTransform: "uppercase" }}>Book your table</p>
					<p className="font-bold m-b-3" style={{ color: "#0f172a", fontSize: 16, lineHeight: 1.05 }}>Choose a time that works for you.</p>
					<div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
						<div style={{ height: 36, borderRadius: 12, background: "#f8fafc", border: "1px solid #e2e8f0" }} />
						<div style={{ height: 36, borderRadius: 12, background: "#f8fafc", border: "1px solid #e2e8f0" }} />
					</div>
					<div style={{ height: 36, borderRadius: 12, background: "#f8fafc", border: "1px solid #e2e8f0", marginBottom: 10 }} />
					<div style={{ height: 42, borderRadius: 14, background: `linear-gradient(135deg, ${websitePrimary}, #111827)` }} />
				</div>
			</div>
		);

		const widgetPreview = stackLayout ? (
			<div style={{ width: "100%", maxWidth: 360, borderRadius: 28, overflow: "hidden", background: "#fff", boxShadow: "0 24px 54px rgba(15,23,42,0.18)" }}>
				<div style={{ padding: "1rem", background: b.banner_url?.trim() ? `linear-gradient(180deg, rgba(15,23,42,0.18), rgba(15,23,42,0.7)), url("${b.banner_url.trim()}") center / cover` : widgetPrimary, display: "flex", alignItems: "center", gap: 10, minHeight: 88 }}>
					<div style={{ width: 38, height: 38, borderRadius: 13, background: "rgba(255,255,255,0.2)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 900, border: "1px solid rgba(255,255,255,0.3)", overflow: "hidden" }}>
						{logo ? <img src={logo} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : letter}
					</div>
					<div>
						<p className="font-bold m-b-0" style={{ color: "#fff", fontSize: 14 }}>{title}</p>
						<p className="tiny m-b-0" style={{ color: "rgba(255,255,255,0.7)" }}>Reservations</p>
					</div>
				</div>
				<div style={{ padding: "1rem" }}>
					<p className="tiny font-bold text-muted m-b-1" style={{ letterSpacing: "0.12em", textTransform: "uppercase" }}>Book your table</p>
					<div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
						<div style={{ height: 32, borderRadius: 12, background: "#f8fafc", border: "1px solid #e2e8f0" }} />
						<div style={{ height: 32, borderRadius: 12, background: "#f8fafc", border: "1px solid #e2e8f0" }} />
					</div>
					<div style={{ height: 38, borderRadius: 13, background: `linear-gradient(135deg, ${widgetPrimary}, #111827)` }} />
				</div>
			</div>
		) : (
			<div style={{ width: "100%", maxWidth: 360, height: 360, borderRadius: 28, background: "#fff", border: "1px solid #e2e8f0", boxShadow: "0 24px 54px rgba(15,23,42,0.16)", position: "relative", overflow: "hidden" }}>
				<div style={{ padding: 14, borderBottom: "1px solid #f1f5f9" }}>
					<div style={{ display: "flex", alignItems: "center", gap: 10 }}>
						<div style={{ width: 32, height: 32, borderRadius: 11, background: widgetPrimary, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 900, overflow: "hidden" }}>
							{logo ? <img src={logo} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : letter}
						</div>
						<div>
							<p className="font-bold m-b-0" style={{ color: "#0f172a", fontSize: 13 }}>{title}</p>
							<p className="tiny text-muted m-b-0">Storefront preview</p>
						</div>
					</div>
				</div>
				<div style={{ position: "absolute", right: widgetPosition === "bottom-left" ? undefined : 16, left: widgetPosition === "bottom-left" ? 16 : undefined, bottom: 78, width: 220, borderRadius: 20, background: "#fff", boxShadow: "0 24px 54px rgba(15,23,42,0.2)", overflow: "hidden", padding: 10 }}>
					{b.banner_url?.trim() ? <div style={{ height: 52, margin: "-10px -10px 8px", background: `url("${b.banner_url.trim()}") center / cover` }} /> : null}
					<p className="tiny font-bold text-muted m-b-1" style={{ letterSpacing: "0.12em", textTransform: "uppercase" }}>Book your table</p>
					<div style={{ height: 28, borderRadius: 10, background: "#f8fafc", border: "1px solid #e2e8f0", marginBottom: 6 }} />
					<div style={{ height: 28, borderRadius: 10, background: "#f8fafc", border: "1px solid #e2e8f0", marginBottom: 8 }} />
					<div style={{ height: 34, borderRadius: 12, background: `linear-gradient(135deg, ${widgetPrimary}, #111827)` }} />
				</div>
				<div style={{ position: "absolute", bottom: 16, [widgetPosition === "bottom-left" ? "left" : "right"]: 16 }}>
					<div style={{ width: widgetButtonStyle === "pill" ? 126 : 56, height: 56, borderRadius: widgetButtonStyle === "square" ? 16 : 999, background: widgetPrimary, boxShadow: `0 14px 30px -12px ${widgetPrimary}`, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 900, letterSpacing: "0.08em" }}>{widgetButtonStyle === "pill" ? "RESERVE" : "R"}</div>
				</div>
			</div>
		);

		return (
			<div>
				<FormGroup title="Shared Branding" help="Branding and form options shared by the booking website and embeddable widget.">
					{renderWebsiteControls()}
				</FormGroup>
				{renderBrandFields()}
				<TabSelect
					id="table-booking-install-tabs"
					className="m-b-4 m-t-4"
					hasBorder={true}
					screenWidth={this.injected.store.view.screen_width}
					onChange={(v) => this.setState({ widgetTab: v.value as "website" | "widget" })}
					value={widgetTab}
					values={[
						{ label: "Booking Website", value: "website" },
						{ label: "Booking Widget", value: "widget" },
					]}
				/>
				{widgetTab === "website" && (
					<div style={{ display: "grid", gridTemplateColumns: stackLayout ? "1fr" : "1fr 1fr", gap: "1.5rem", alignItems: "start" }}>
						<div>
							<p className="small m-b-3 lhp">Share this direct booking website with guests.</p>
							<FormGroup title="Booking Website URL" help="Direct link to your standalone reservation page.">
								<div className="flex-line gap-2">
									<Input type="text" value={bookingUrl} readOnly />
									<Button type="button" color="white" onClick={() => this.copyText(bookingUrl)}>Copy</Button>
								</div>
							</FormGroup>
							<FastField
								name="widget.branding.primary_color"
								render={({ field }: any) => (
									<FormGroup optional={true} title="Primary Color" help="Accent color for buttons and highlights on the booking website.">
										<div className="flex-line gap-2">
											<Input
												type="color"
												style={{ width: 48, height: 40, padding: 4, cursor: "pointer" }}
												value={field.value || websitePrimary}
												onChange={(e) => setFieldValue("widget.branding.primary_color", e.target.value)}
											/>
											<Input type="text" placeholder="#a85d44" {...field} value={field.value || ""} />
										</div>
									</FormGroup>
								)}
							/>
						</div>
						<div style={{ background: "#f1f5f9", borderRadius: 32, padding: stackLayout ? "1rem" : "1.25rem", display: "flex", alignItems: "flex-start", justifyContent: "center" }}>
							{websitePreview}
						</div>
					</div>
				)}
				{widgetTab === "widget" && (
					<div style={{ display: "grid", gridTemplateColumns: stackLayout ? "1fr" : "1fr 1fr", gap: "1.5rem", alignItems: "start" }}>
						<div>
							<p className="small m-b-3 lhp">Install this script on another website to show a floating reservation launcher.</p>
							{renderWidgetControls()}
						</div>
					
						<div>
							<div style={{ background: "#f1f5f9", borderRadius: 32, padding: stackLayout ? "1rem" : "1.25rem", display: "flex", alignItems: "flex-start", justifyContent: "center" }}>
								{widgetPreview}
							</div>
							{settingRow(
									<FormGroup title="Embed Code" help="Paste this script before the closing </body> tag on any external site. If that site sends a Content-Security-Policy, it must allow this storefront in both script-src and frame-src.">
										{hasStorefrontDomain ? (
											<div style={{ border: "1px solid #e2e8f0", borderRadius: 18, background: "#0f172a", overflow: "hidden", boxShadow: "0 14px 30px rgba(15,23,42,0.08)" }}>
												<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0.7rem 0.85rem", borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
													<span className="tiny font-bold" style={{ color: "#cbd5e1", letterSpacing: "0.12em", textTransform: "uppercase" }}>Install snippet</span>
													<Button type="button" color="white" size="xxs" onClick={() => this.copyText(script)}>Copy code</Button>
												</div>
												<pre style={{ margin: 0, padding: "0.9rem", color: "#e2e8f0", fontSize: 12, lineHeight: 1.55, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{script}</pre>
											</div>
										) : (
											<div style={{ border: "1px solid #fecaca", borderRadius: 18, background: "#fef2f2", padding: "0.9rem", color: "#991b1b", fontSize: 13, lineHeight: 1.55 }}>
												This restaurant has no storefront domain yet, so an embed snippet would point nowhere and the widget would never appear. Set a custom domain or subdomain first, then come back here.
											</div>
										)}
									</FormGroup>
								, { borderTop: false })}
						</div>
					</div>
				)}
			</div>
		);
	}

	render() {
		const { tab } = this.state;
		const r = this.injected.store.restaurant!;
		const ri = r.reservations_integration;
		const masterOn = ri?.enabled === true;
		const widgetOn = masterOn && ri?.widget_enabled !== false;
		const tabValues = [
			{ label: "General", value: "0" },
			{ label: "Booking Timings", value: "3" },
			{ label: "Available Hours", value: "4" },
			{ label: "Custom Booking Fields", value: "5" },
			...(masterOn ? [{ label: "Logic", value: "1" }] : []),
			...(widgetOn ? [{ label: "Install", value: "2" }] : []),
		];
		return (
			<div>
				<TabSelect
					id="accounts-tab-options"
					className="border-white-tb-10"
					hasBorder={true}
					screenWidth={this.injected.store.view.screen_width}
					onChange={(v) => this.setState({ tab: v.value })}
					value={tab}
					values={tabValues} />
				<RestaurantForm<FormValues>
					submit={async (r, values) => {
						const topNav = r.website?.sections?.top_nav as T.ObjectAny | undefined;
						const websiteLogo = topNav?.image_logo || topNav?.images?.logo;
						const websiteLogoUrl = websiteLogo
							? CoreUtils.image.uc(websiteLogo, { format: "auto" })
							: "";
						if (websiteLogoUrl && !(values.widget?.branding?.logo_url || "").trim()) {
							values = {
								...values,
								widget: {
									...(values.widget || {}),
									branding: {
										...(values.widget?.branding || {}),
										logo_url: websiteLogoUrl,
									},
								},
							};
						}
						r.settings.services.table_booking = values;
						const update = { $set: { "settings.services.table_booking": r.settings.services.table_booking } };
						return { r, update };
					}}
					initialValues={this.initialValues}
					onSuccess={async () => await updateRestaurantNotificationOptions(this.injected.store)}
					onError={() => UI.notification.error("An error occurred")}
					onSuccessMessage="Settings Updated"
				>
					{({ form, error, getFieldError }) => {
						const { isSubmitting, setFieldValue } = form;
						return (
							<div className="p-4">

								<FormGroup>
									{tab === "0" && (
										<div>
											<FastField
												name="enabled"
												render={({ field }: any) => (
													<FormGroup
														title="Enabled"
														help="Allows customers to making a table booking online">
														<Switch
															id="enable-pickup-switch"
															checked={field.value}
															onChange={(e) => setFieldValue("enabled", e.target.checked)} />
													</FormGroup>
												)}
											/>
											<FastField
												name="display_name"
												render={({ field }: any) => (
													<FormGroup
														optional={true}
														title="Display Name"
														help="Override the service name. This will override the translation as well. Defaults to Table Booking."
													>
														<Input
															type="text"
															{...field}
														/>
													</FormGroup>
												)}
											/>
											<FastField
												name="notes"
												render={({ field }: any) => (
													<FormGroup
														optional={true}
														title="Notes"
														help="This will be shown to customers if they choose this order method">
														<Input type="text" {...field} />
													</FormGroup>
												)}
											/>
											<FormGroup>
												<FastField
													name="min_people"
													render={({ field }: any) => (
														<FormGroup
															{...this.colProps}
															optional={true}
															title="Minimum Number Of People"
															help="Minimum party size on the booking form. Leave blank for 1. Slot availability still depends on free tables at that time.">
															<Input
																type="number"
																min={1}
																step={1}
																value={field.value || ""}
																{...field}
															/>
														</FormGroup>
													)}
												/>
												<FastField
													name="max_people"
													render={({ field }: any) => (
														<FormGroup
															{...this.colProps}
															optional={true}
															title="Maximum Number Of People"
															help="Maximum party size on the booking form. Also capped by your dine-in tables: we seat up to 3 joinable tables per booking (adjacent or linked on the floor plan), not total seats in the venue. Busy slots may block large parties even under this max.">
															<Input
																type="number"
																min={1}
																step={1}
																value={field.value || form.values.limit_number_of_people || ""}
																{...field}
															/>
														</FormGroup>
													)}
												/>

											</FormGroup>
										</div>
									)}
									{tab === "1" && (
										<div>
											<FastField
												name="booking_logic.auto_confirm_strategy"
												render={({ field }: any) => (
													<FormGroup
														title="Auto-Confirmation Strategy"
														help="Controls automatic confirmation when limits allow (existing status timers still apply).">
														<SelectAdv
															type="single"
															shadow="one"
															value={field.value || "off"}
															onChange={(v: string) =>
																setFieldValue("booking_logic.auto_confirm_strategy", v)
															}
															options={AUTO_CONFIRM_STRATEGY_OPTIONS}
														/>
													</FormGroup>
												)}
											/>
											<FormGroup contentClassName="grid-2 sm sm-gap">
												<FastField
													name="booking_logic.auto_confirm_threshold"
													render={({ field }: any) => (
														<FormGroup
															{...this.colProps}
															optional={true}
															title="Smart Capacity Threshold (%)"
															help="Smart mode auto-confirms below this booked-capacity percentage; at or above it, bookings stay unconfirmed.">
															<Input
																type="number"
																min={1}
																max={100}
																value={field.value ?? ""}
																onChange={(e) =>
																	setFieldValue(
																		"booking_logic.auto_confirm_threshold",
																		e.target.value === "" ? undefined : Number(e.target.value)
																	)
																}
															/>
														</FormGroup>
													)}
												/>
												<FastField
													name="booking_logic.default_turn_minutes"
													render={({ field }: any) => (
														<FormGroup
															{...this.colProps}
															optional={true}
															title="Default Turn Length (Minutes)"
															help="Used for table overlap checks, auto-assignment on guest booking (Always auto-confirm), and dashboard calendar defaults.">
															<Input
																type="number"
																min={15}
																step={15}
																value={field.value ?? ""}
																onChange={(e) =>
																	setFieldValue(
																		"booking_logic.default_turn_minutes",
																		e.target.value === "" ? undefined : Number(e.target.value)
																	)
																}
															/>
														</FormGroup>
													)}
												/>
											</FormGroup>
										</div>
									)}
									{tab === "2" && this.renderInstallTab(form, setFieldValue)}
									{tab === "3" && <ServiceOrderTimeFields service="table_booking" form={form} />}
									{tab === "4" && <ServiceHoursField service="table_booking" error={(field) => getFieldError(form, field) as string | null | undefined} />}
									{tab === "5" && <CustomCheckoutField service="table_booking" form={form} />}
								</FormGroup>

								{error && <FormGroup error={error} />}

								<Button full={true} color="primary" type="submit" disabled={isSubmitting}>
									{isSubmitting && <RotateLoader size={2} color="white" />}
									{!isSubmitting && "Save"}
								</Button>

							</div>
						);
					}}
				</RestaurantForm>
			</div>
		);
	}

}
